// PLTCP - Public Land Tree Cutting Permit - previously known as PLTP because of confusion XD
// ISSUANCE OF TREE CUTTING PERMIT FOR PLANTED TREES AND NATURALLY GROWING TREES FOUND WITHIN *PUBLIC PLACES* (PLAZA, PUBLIC PARKS, SCHOOL PREMISES OR POLITICAL SUBDIVISIONS) FOR PURPOSES OF PUBLIC SAFETY
// This Permit serves as proof of authorization for the removal/cutting of trees in public places (Plaza, Public Parks, School Premises or Political Subdivisions for purposes of public safety).

import {
   AlertDialog,
   AlertDialogAction,
   AlertDialogCancel,
   AlertDialogContent,
   AlertDialogDescription,
   AlertDialogFooter,
   AlertDialogHeader,
   AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import Modal from '@/components/ui/modal';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import '@/components/ui/styles/customScrollBar.css';
import { Textarea } from '@/components/ui/textarea';
import { CheckboxItem, formatLabel, formatReviewValue, UploadCard } from '@/pages/user/ApplicationForms/CSAWForm/CSAWFormUtils';
import { gql, useLazyQuery, useMutation } from '@apollo/client';
import { Loader2 } from "lucide-react";
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import FormStepIndicator from '../FormStepIndicator';

const CREATE_PLTCP_PERMIT = gql`
   mutation CreatePLTCPPermit($input: PLTCPPermitInput!) {
      createPLTCPPermit(input: $input) {
         id
         applicationNumber
         status
         dateOfSubmission
         requestType
         name
         address
         contactNumber
         treeType
         treeStatus
         landType
         posingDanger
         forPersonalUse
         purpose
         files {
         applicationLetter { filename contentType }
         lguEndorsement { filename contentType }
         homeownersResolution { filename contentType }
         ptaResolution { filename contentType }
         }
      }
   }
   `;

const SAVE_PLTCP_PERMIT_DRAFT = gql`
   mutation SavePLTCPPermitDraft($input: PLTCPPermitInput!) {
      savePLTCPPermitDraft(input: $input) {
         id
         applicationNumber
         status
         files {
         applicationLetter { filename contentType }
         lguEndorsement { filename contentType }
         homeownersResolution { filename contentType }
         ptaResolution { filename contentType }
         }
      }
   }
   `;

const VERIFY_INVENTORY_REQUEST = gql`
   query VerifyInventoryRequest($applicationNumber: String!, $requestType: String!) {
      verifyInventoryRequest(applicationNumber: $applicationNumber, requestType: $requestType) {
         ... on PLTCPPermit {
         id
         applicationNumber
         requestType
         ApprovedInventoryRequest
         name
         address
         contactNumber
         treeType
         treeStatus
         landType
         posingDanger
         forPersonalUse
         purpose
         projectLocation
         projectAddress
         occupation
         }
      }
   }
`;

const PLTCPForm = () => {
   const navigate = useNavigate();
   const [currentStep, setCurrentStep] = useState(() => {
      const savedStep = localStorage.getItem('pltcpFormStep');
      return savedStep ? parseInt(savedStep, 10) : 0;
   });
   const [formData, setFormData] = useState(() => {
      const savedFormData = localStorage.getItem('pltcpFormData');
      return savedFormData ? JSON.parse(savedFormData) : {
         applicationType: 'Public Land Tree Cutting Permit',
         name: '',
         address: '',
         contactNumber: '',
         projectLocation: '',
         projectAddress: '',
         occupation: '',
         requestType: '',
         treeType: [],
         treeStatus: [],
         landType: [],
         posingDanger: false,
         forPersonalUse: false,
         purpose: '',
         isWithinSubdivision: false,
         isSchoolOrOrganization: false,
         files: {
            letterOfIntent: [],
            applicationLetter: [],
            lguEndorsement: [],
            homeownersResolution: [],
            ptaResolution: []
         },
         dateOfSubmission: '',
         status: '',
      };
   });
   const [modalOpen, setModalOpen] = useState(false);
   const [modalContent, setModalContent] = useState({ title: '', message: '' });
   const [createPLTCPPermit] = useMutation(CREATE_PLTCP_PERMIT);
   const [savePLTCPPermitDraft] = useMutation(SAVE_PLTCP_PERMIT_DRAFT);
   const [isSubmitting, setIsSubmitting] = useState(false);
   const [isSavingDraft, setIsSavingDraft] = useState(false);
   const [showSubmitDialog, setShowSubmitDialog] = useState(false);
   const [showDraftDialog, setShowDraftDialog] = useState(false);
   const [previousApplicationNumber, setPreviousApplicationNumber] = useState('');
   const [isVerifyingRequest, setIsVerifyingRequest] = useState(false);
   const [verifiedInventoryData, setVerifiedInventoryData] = useState(null);
   const [verifyInventoryRequest] = useLazyQuery(VERIFY_INVENTORY_REQUEST, {
      onCompleted: (data) => {
         if (data?.verifyInventoryRequest) {
            setVerifiedInventoryData(data.verifyInventoryRequest);
            toast.success('Inventory request verified successfully', {
               description: 'You can now proceed with your cutting permit application'
            });
            // Pre-fill form data from inventory request
            setFormData(prev => ({
               ...prev,
               name: data.verifyInventoryRequest.name,
               address: data.verifyInventoryRequest.address,
               contactNumber: data.verifyInventoryRequest.contactNumber,
               treeType: data.verifyInventoryRequest.treeType,
               treeStatus: data.verifyInventoryRequest.treeStatus,
               landType: data.verifyInventoryRequest.landType,
               posingDanger: data.verifyInventoryRequest.posingDanger,
               forPersonalUse: data.verifyInventoryRequest.forPersonalUse,
               purpose: data.verifyInventoryRequest.purpose,
               projectLocation: data.verifyInventoryRequest.projectLocation,
               projectAddress: data.verifyInventoryRequest.projectAddress,
               occupation: data.verifyInventoryRequest.occupation
            }));
         }
         setIsVerifyingRequest(false);
      },
      onError: (error) => {
         toast.error(error.message || 'Failed to verify inventory request');
         setIsVerifyingRequest(false);
         setPreviousApplicationNumber('');
      }
   });

   const handleInputChange = (e) => {
      const { name, value } = e.target;
      setFormData(prev => ({ ...prev, [name]: value }));
   };

   const handleCheckboxChange = (e) => {
      const { name, checked } = e.target;

      // Map of checkbox names to their corresponding file types
      const fileTypeMap = {
         isWithinSubdivision: 'homeownersResolution',
         isSchoolOrOrganization: 'ptaResolution'
      };

      // Clear associated files if requirement is unchecked
      if (!checked && fileTypeMap[name]) {
         setFormData(prev => ({
            ...prev,
            [name]: checked,
            files: {
               ...prev.files,
               [fileTypeMap[name]]: [] // Clear the files for this requirement
            }
         }));
      } else {
         setFormData(prev => ({ ...prev, [name]: checked }));
      }
   };

   const handleFileChange = (e, documentType) => {
      if (e.target.files) {
         const newFiles = Array.from(e.target.files);
         setFormData(prev => ({
            ...prev,
            files: {
               ...prev.files,
               [documentType]: [...prev.files[documentType], ...newFiles]
            }
         }));
      }
   };

   const removeFile = (documentType, fileToRemove) => {
      setFormData(prev => ({
         ...prev,
         files: {
            ...prev.files,
            [documentType]: prev.files[documentType].filter(file => file !== fileToRemove)
         }
      }));
   };

   const handleNextStep = () => {
      if (currentStep === 0) {
         if (!formData.requestType) {
            toast.error("Please select a request type");
            return;
         }
         const verificationEnabled = localStorage.getItem('verificationEnabled') !== 'false';
         if (verificationEnabled && formData.requestType === 'Cutting' && !verifiedInventoryData) {
            toast.error("Please verify your previous inventory request first");
            return;
         }
      }

      // Skip Tree and Land Details step for Cutting requests
      if (currentStep === 1 && formData.requestType === 'Cutting') {
         setCurrentStep(3); // Skip to Document Requirements
      } else if (currentStep === 2 && formData.requestType === 'Inventory') {
         setCurrentStep(4); // Skip to Upload Documents for Inventory
      } else {
         setCurrentStep(prev => prev + 1);
      }
   };

   const handlePrevStep = () => {
      // Handle going back from Document Requirements to Applicant Details for Cutting requests
      if (currentStep === 3 && formData.requestType === 'Cutting') {
         setCurrentStep(1);
      } else if (currentStep === 4 && formData.requestType === 'Inventory') {
         setCurrentStep(2);
      } else {
         setCurrentStep(prev => prev - 1);
      }
   };

   const handleSaveAsDraft = () => {
      setShowDraftDialog(true);
   };

   const handleConfirmSaveAsDraft = async () => {
      try {
         setIsSavingDraft(true);
         const input = {
            requestType: formData.requestType,
            name: formData.name,
            address: formData.address,
            contactNumber: formData.contactNumber,
            treeType: formData.treeType,
            treeStatus: formData.treeStatus,
            landType: formData.landType,
            posingDanger: formData.posingDanger,
            forPersonalUse: formData.forPersonalUse,
            purpose: formData.purpose,
            isWithinSubdivision: Boolean(formData.isWithinSubdivision),
            isSchoolOrOrganization: Boolean(formData.isSchoolOrOrganization),
            files: {}
         };

         // Process files
         // for (const [key, files] of Object.entries(formData.files)) {
         //    if (files.length > 0) {
         //       input.files[key] = await Promise.all(files.map(async (file) => {
         //          if (file instanceof File) {
         //             const content = await readFileAsBase64(file);
         //             return {
         //                filename: file.name,
         //                contentType: file.type,
         //                data: content
         //             };
         //          } else {
         //             return {
         //                filename: file.filename,
         //                contentType: file.contentType,
         //                // Don't include data if it's not a File object
         //             };
         //          }
         //       }));
         //    }
         // }
         for (const [key, files] of Object.entries(formData.files)) {
            if (files.length > 0) {
               input.files[key] = await Promise.all(files.map(async (file) => {
                  const content = await readFileAsBase64(file);
                  return {
                     filename: file.name,
                     contentType: file.type,
                     data: content
                  };
               }));
            }
         }

         const token = localStorage.getItem('token');
         const { data } = await savePLTCPPermitDraft({
            variables: { input },
            context: {
               headers: {
                  'Apollo-Require-Preflight': 'true',
                  'Authorization': `Bearer ${token}`,
               },
            },
         });

         if (data.savePLTCPPermitDraft) {
            setModalContent({
               title: 'Draft saved successfully!',
               message: 'Do you want to view your applications?'
            });
            setModalOpen(true);

            localStorage.removeItem('pltcpFormStep');
            localStorage.removeItem('pltcpFormData');
         }
      } catch (error) {
         console.error('Error saving draft:', error);
         toast.error("Error saving draft: " + error.message);
      } finally {
         setIsSavingDraft(false);
         setShowDraftDialog(false);
      }
   };

   const handleSubmit = async (e) => {
      if (e) e.preventDefault();
      setShowSubmitDialog(true);
   };

   const handleConfirmSubmit = async () => {
      try {
         setIsSubmitting(true);
         const input = {
            requestType: formData.requestType,
            name: formData.name,
            address: formData.address,
            contactNumber: formData.contactNumber,
            projectLocation: formData.projectLocation,
            projectAddress: formData.projectAddress,
            occupation: formData.occupation,
            treeType: formData.treeType,
            treeStatus: formData.treeStatus,
            landType: formData.landType,
            posingDanger: formData.posingDanger,
            forPersonalUse: formData.forPersonalUse,
            purpose: formData.purpose,
            isWithinSubdivision: Boolean(formData.isWithinSubdivision),
            isSchoolOrOrganization: Boolean(formData.isSchoolOrOrganization),
            files: {},
            verifiedInventoryId: verifiedInventoryData?.id || null
         };

         // Process files
         for (const [key, files] of Object.entries(formData.files)) {
            if (files.length > 0) {
               input.files[key] = await Promise.all(files.map(async (file) => {
                  const content = await readFileAsBase64(file);
                  return {
                     filename: file.name,
                     contentType: file.type,
                     data: content
                  };
               }));
            }
         }

         console.log('Submitting input:', input);

         const token = localStorage.getItem('token');
         const { data } = await createPLTCPPermit({
            variables: { input },
            context: {
               headers: {
                  'Apollo-Require-Preflight': 'true',
                  'Authorization': `Bearer ${token}`,
               },
            },
         });

         if (data.createPLTCPPermit) {
            setModalContent({
               title: 'Application submitted successfully!',
               message: 'Do you want to view your application?'
            });
            setModalOpen(true);

            localStorage.removeItem('pltcpFormStep');
            localStorage.removeItem('pltcpFormData');
         }
      } catch (error) {
         console.error('Error submitting application:', error);
         toast.error("Error submitting application: " + error.message);
      } finally {
         setIsSubmitting(false);
         setShowSubmitDialog(false);
      }
   };

   // Helper function to read file as base64
   const readFileAsBase64 = (file) => {
      return new Promise((resolve, reject) => {
         const reader = new FileReader();
         reader.onload = () => resolve(reader.result.split(',')[1]);
         reader.onerror = error => reject(error);
         reader.readAsDataURL(file);
      });
   };

   const steps = [
      { title: "Request Type", description: "Select the type of request" },
      { title: "Applicant Details", description: "Fill in your personal information" },
      {
         title: formData.requestType === 'Cutting' ? "Purpose" : "Tree and Land Details",
         description: formData.requestType === 'Cutting' ? "Specify the purpose for cutting" : "Provide information about the trees and land"
      },
      { title: "Document Requirements", description: "Specify document requirements" },
      { title: "Upload Documents", description: "Upload necessary documents" },
      { title: "Review Your Application", description: "Review and submit your application" },
   ];

   useEffect(() => {
      localStorage.setItem('pltcpFormStep', currentStep.toString());
   }, [currentStep]);

   useEffect(() => {
      localStorage.setItem('pltcpFormData', JSON.stringify(formData));
   }, [formData]);

   useEffect(() => {
      const handleBeforeUnload = (e) => {
         // If it's a refresh, persist the data
         if (e.persisted) {
            return;
         }
      };

      window.addEventListener('beforeunload', handleBeforeUnload);

      return () => {
         window.removeEventListener('beforeunload', handleBeforeUnload);

         // Get the navigation type
         const perfEntries = performance.getEntriesByType('navigation');
         const navType = perfEntries.length > 0 ? perfEntries[0].type : '';

         // Only clear if it's not a refresh/reload
         if (navType !== 'reload') {
            // Clear form data from localStorage when component unmounts (not on refresh)
            localStorage.removeItem('pltcpFormStep');
            localStorage.removeItem('pltcpFormData');
            // Reset form data to initial state
            setFormData({
               applicationType: 'Private Land Tree Cutting Permit',
               name: '',
               address: '',
               cellphone: '',
               purpose: '',
               isWithinTimberland: false,
               isTitledProperty: false,
               isWithinProtectedArea: false,
               documentRequirements: {
                  isWithinTimberland: false,
                  isTitledProperty: false,
                  isWithinProtectedArea: false
               },
               files: {
                  letterOfIntent: [],
                  validId: [],
                  landTenurial: [],
                  ownerConsent: [],
                  pambClearance: [],
                  proofOfOwnership: [],
                  sketchPlan: [],
                  plantationLocation: [],
                  inventoryReport: []
               },
               dateOfSubmission: '',
               status: ''
            });
            // Reset current step
            setCurrentStep(0);
         }
      };
   }, []);

   const handleVerifyInventoryRequest = async () => {
      if (!previousApplicationNumber) {
         toast.error("Please enter an application number");
         return;
      }

      // Proper regex pattern for PLTCP application numbers
      const permitTypeMatch = previousApplicationNumber.match(/^PMDQ-PLTCP-\d{4}-\d{4}-\d{6}$/);
      if (!permitTypeMatch) {
         toast.error("Invalid PLTCP application number format");
         return;
      }

      setIsVerifyingRequest(true);
      try {
         await verifyInventoryRequest({
            variables: {
               applicationNumber: previousApplicationNumber,
               requestType: formData.requestType
            }
         });
      } catch (error) {
         console.error('Error verifying inventory request:', error);
         toast.error(error.message || 'Failed to verify inventory request');
         setPreviousApplicationNumber('');
      }
   };

   const renderRequestTypeStep = () => (
      <div className="space-y-4">
         <RadioGroup
            name="requestType"
            value={formData.requestType}
            onValueChange={(value) => handleInputChange({ target: { name: 'requestType', value } })}
            className="space-y-4"
         >
            <div className="flex items-center space-x-2 p-4 rounded-lg border border-input bg-background hover:bg-accent">
               <RadioGroupItem value="Cutting" id="Cutting" />
               <Label htmlFor="Cutting" className="flex-grow text-foreground">
                  Request for Tree Cutting
               </Label>
            </div>
            <div className="flex items-center space-x-2 p-4 rounded-lg border border-input bg-background hover:bg-accent">
               <RadioGroupItem value="Inventory" id="Inventory" />
               <Label htmlFor="Inventory" className="flex-grow text-foreground">
                  Request for Tree Inventory
               </Label>
            </div>
         </RadioGroup>

         {formData.requestType === 'Cutting' && (
            <div className="mt-6">
               <div className="bg-blue-50 dark:bg-blue-900/20 rounded-md p-4 mb-6">
                  <div className="flex items-start">
                     <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                           <circle cx="12" cy="12" r="10" />
                           <line x1="12" y1="16" x2="12" y2="12" />
                           <line x1="12" y1="8" x2="12.01" y2="8" />
                        </svg>
                     </div>
                     <div className="ml-3">
                        <h3 className="text-sm font-medium text-blue-800 dark:text-blue-300">
                           Before you proceed with Cutting request
                        </h3>
                        <div className="mt-2 text-sm text-blue-700 dark:text-blue-400">
                           <ul className="list-disc pl-5 space-y-1">
                              <li>You must have a completed inventory request</li>
                              <li>The inventory request must be under your name</li>
                              <li>The application number format should be: PMDQ-PLTCP-XXXX</li>
                              <li>
                                 You can check your completed inventory requests{' '}
                                 <Link
                                    to="/applicationsStatus?tab=Applications&subtab=Completed"
                                    className="text-blue-600 dark:text-blue-300 hover:underline font-medium inline-flex items-center gap-1"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                 >
                                    <span>click here to view</span>
                                    <svg
                                       className="h-4 w-4"
                                       xmlns="http://www.w3.org/2000/svg"
                                       viewBox="0 0 24 24"
                                       fill="none"
                                       stroke="currentColor"
                                       strokeWidth="2"
                                       strokeLinecap="round"
                                       strokeLinejoin="round"
                                    >
                                       <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                                       <polyline points="15 3 21 3 21 9" />
                                       <line x1="10" y1="14" x2="21" y2="3" />
                                    </svg>
                                 </Link>
                              </li>
                           </ul>
                        </div>
                     </div>
                  </div>
               </div>

               <div className="space-y-3">
                  <Label className="text-base font-semibold">
                     Previous Inventory Application Number
                  </Label>
                  <div className="flex gap-3">
                     <Input
                        type="text"
                        value={previousApplicationNumber}
                        onChange={(e) => setPreviousApplicationNumber(e.target.value)}
                        placeholder="e.g., PMDQ-PLTCP-2024-0001"
                        disabled={isVerifyingRequest || verifiedInventoryData}
                        required
                        className="h-11 text-base"
                     />
                     <Button
                        type="button"
                        variant={verifiedInventoryData ? "outline" : "default"}
                        size="lg"
                        onClick={handleVerifyInventoryRequest}
                        disabled={isVerifyingRequest || verifiedInventoryData || !previousApplicationNumber.trim()}
                        className={`min-w-[120px] ${verifiedInventoryData
                           ? "border-green-500 text-green-600"
                           : "bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white"
                           }`}
                     >
                        {isVerifyingRequest ? (
                           <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Verifying
                           </>
                        ) : verifiedInventoryData ? (
                           <>
                              <svg className="mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                 <path d="M20 6L9 17l-5-5" />
                              </svg>
                              Verified
                           </>
                        ) : (
                           'Verify Request'
                        )}
                     </Button>
                  </div>
               </div>
            </div>
         )}
      </div>
   );

   const renderTreeAndLandDetails = () => (
      <div className="space-y-6 h-[500px] overflow-y-auto p-4 custom-scrollbar">
         {formData.requestType === 'Inventory' ? (
            <>
               <div>
                  <Label className="text-lg font-semibold text-green-700 dark:text-green-500">Tree Type</Label>
                  <div className="mt-2 space-y-2">
                     <div className="flex items-center space-x-2">
                        <Checkbox
                           id="plantedTrees"
                           checked={formData.treeType?.includes('Planted Trees')}
                           onCheckedChange={(checked) => {
                              const newTreeType = checked
                                 ? [...(formData.treeType || []), 'Planted Trees']
                                 : (formData.treeType || []).filter(type => type !== 'Planted Trees');
                              setFormData(prev => ({ ...prev, treeType: newTreeType }));
                           }}
                        />
                        <Label htmlFor="plantedTrees" className="text-sm font-medium text-foreground">Planted Trees</Label>
                     </div>
                     <div className="flex items-center space-x-2">
                        <Checkbox
                           id="naturallyGrown"
                           checked={formData.treeType?.includes('Naturally Grown')}
                           onCheckedChange={(checked) => {
                              const newTreeType = checked
                                 ? [...(formData.treeType || []), 'Naturally Grown']
                                 : (formData.treeType || []).filter(type => type !== 'Naturally Grown');
                              setFormData(prev => ({ ...prev, treeType: newTreeType }));
                           }}
                        />
                        <Label htmlFor="naturallyGrown" className="text-sm font-medium text-foreground">Naturally Grown</Label>
                     </div>
                  </div>
               </div>

               <div>
                  <Label className="text-lg font-semibold text-green-700 dark:text-green-500">Tree Status</Label>
                  <div className="mt-2 space-y-2">
                     <div className="flex items-center space-x-2">
                        <Checkbox
                           id="standing"
                           checked={formData.treeStatus?.includes('Standing')}
                           onCheckedChange={(checked) => {
                              const newTreeStatus = checked
                                 ? [...(formData.treeStatus || []), 'Standing']
                                 : (formData.treeStatus || []).filter(status => status !== 'Standing');
                              setFormData(prev => ({ ...prev, treeStatus: newTreeStatus }));
                           }}
                        />
                        <Label htmlFor="standing" className="text-sm font-medium text-foreground">Standing</Label>
                     </div>
                     <div className="flex items-center space-x-2">
                        <Checkbox
                           id="blownDown"
                           checked={formData.treeStatus?.includes('Blown Down')}
                           onCheckedChange={(checked) => {
                              const newTreeStatus = checked
                                 ? [...(formData.treeStatus || []), 'Blown Down']
                                 : (formData.treeStatus || []).filter(status => status !== 'Blown Down');
                              setFormData(prev => ({ ...prev, treeStatus: newTreeStatus }));
                           }}
                        />
                        <Label htmlFor="blownDown" className="text-sm font-medium text-foreground">Blown Down</Label>
                     </div>
                  </div>
               </div>

               <div>
                  <Label className="text-lg font-semibold text-green-700 dark:text-green-500">Land Type</Label>
                  <div className="mt-2 space-y-2">
                     <div className="flex items-center space-x-2">
                        <Checkbox
                           id="privateLand"
                           checked={formData.landType?.includes('Within Private Land')}
                           onCheckedChange={(checked) => {
                              const newLandType = checked
                                 ? [...(formData.landType || []), 'Within Private Land']
                                 : (formData.landType || []).filter(type => type !== 'Within Private Land');
                              setFormData(prev => ({ ...prev, landType: newLandType }));
                           }}
                        />
                        <Label htmlFor="privateLand" className="text-sm font-medium text-foreground">Within Private Land</Label>
                     </div>
                     <div className="flex items-center space-x-2">
                        <Checkbox
                           id="tenuredForestLand"
                           checked={formData.landType?.includes('Within Tenured Forest Land')}
                           onCheckedChange={(checked) => {
                              const newLandType = checked
                                 ? [...(formData.landType || []), 'Within Tenured Forest Land']
                                 : (formData.landType || []).filter(type => type !== 'Within Tenured Forest Land');
                              setFormData(prev => ({ ...prev, landType: newLandType }));
                           }}
                        />
                        <Label htmlFor="tenuredForestLand" className="text-sm font-medium text-foreground">Within Tenured Forest Land</Label>
                     </div>
                  </div>
               </div>

               <div className="space-y-2">
                  <Label className="text-lg font-semibold text-green-700 dark:text-green-500">Additional Characteristics</Label>
                  <div className="flex items-center space-x-2 mt-2">
                     <Checkbox
                        id="posingDanger"
                        checked={formData.posingDanger}
                        onCheckedChange={(checked) => setFormData(prev => ({ ...prev, posingDanger: checked }))}
                     />
                     <Label htmlFor="posingDanger" className="text-sm font-medium text-foreground">Posing Danger</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                     <Checkbox
                        id="forPersonalUse"
                        checked={formData.forPersonalUse}
                        onCheckedChange={(checked) => setFormData(prev => ({ ...prev, forPersonalUse: checked }))}
                     />
                     <Label htmlFor="forPersonalUse" className="text-sm font-medium text-foreground">For Personal Use</Label>
                  </div>
               </div>

               <div>
                  <Label htmlFor="purpose" className="text-lg font-semibold text-green-700 dark:text-green-500">Purpose</Label>
                  <Textarea
                     id="purpose"
                     name="purpose"
                     value={formData.purpose}
                     onChange={handleInputChange}
                     required
                     className="mt-2 bg-background"
                  />
               </div>
            </>
         ) : (
            <div className="space-y-4">
               <div>
                  <Label htmlFor="purpose" className="text-lg font-semibold text-green-700 dark:text-green-500">Purpose</Label>
                  <Textarea
                     id="purpose"
                     name="purpose"
                     value={formData.purpose}
                     onChange={handleInputChange}
                     required
                     className="mt-2 bg-background"
                  />
               </div>
            </div>
         )}
      </div>
   );

   return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white dark:from-gray-900 dark:to-gray-800 transition-colors duration-300 flex flex-col justify-between pt-[83px]">
         <div className="container mx-auto px-4 flex-grow">
            <h1 className="text-3xl font-[700] text-green-800 dark:text-green-500 mb-6 text-center">
               Public Land Tree Cutting Permit Application
            </h1>
            <div className="max-w-2xl mx-auto mb-6">
               <FormStepIndicator currentStep={currentStep + 1} formType="PLTCP" />
            </div>
            <Card className="max-w-2xl mx-auto shadow-lg bg-background">
               <CardHeader>
                  <CardTitle className="text-foreground">{steps[currentStep].title}</CardTitle>
               </CardHeader>
               <CardContent>
                  <form onSubmit={handleSubmit}>
                     {currentStep === 0 && renderRequestTypeStep()}

                     {currentStep === 1 && (
                        <div className="space-y-4">
                           <div>
                              <Label htmlFor="name">Full Name</Label>
                              <Input
                                 id="name"
                                 name="name"
                                 value={formData.name}
                                 onChange={handleInputChange}
                                 required
                                 className="mt-1"
                              />
                           </div>
                           <div>
                              <Label htmlFor="address">Complete Address</Label>
                              <Input
                                 id="address"
                                 name="address"
                                 value={formData.address}
                                 onChange={handleInputChange}
                                 required
                                 className="mt-1"
                              />
                           </div>
                           <div>
                              <Label htmlFor="contactNumber">Contact Number</Label>
                              <Input
                                 id="contactNumber"
                                 name="contactNumber"
                                 value={formData.contactNumber}
                                 onChange={handleInputChange}
                                 required
                                 className="mt-1"
                                 type="tel"
                                 pattern="[0-9]*"
                              />
                           </div>
                           <div>
                              <Label htmlFor="occupation">
                                 Occupation
                                 <span className="text-sm text-muted-foreground ml-2">(if applicable)</span>
                              </Label>
                              <Input
                                 id="occupation"
                                 name="occupation"
                                 value={formData.occupation}
                                 onChange={handleInputChange}
                                 className="mt-1"
                                 placeholder="e.g., Principal, Teacher, etc."
                              />
                           </div>
                           <div>
                              <Label htmlFor="projectLocation">Project Location</Label>
                              <Input
                                 id="projectLocation"
                                 name="projectLocation"
                                 value={formData.projectLocation}
                                 onChange={handleInputChange}
                                 required
                                 className="mt-1"
                                 placeholder="e.g., Boac South Central School, Plaza, or Subdivision name"
                              />
                              <p className="text-sm text-muted-foreground mt-1">
                                 Specify the exact location where the trees are located (School, Plaza, or Public Area)
                              </p>
                           </div>
                           <div>
                              <Label htmlFor="projectAddress">Project Address</Label>
                              <Input
                                 id="projectAddress"
                                 name="projectAddress"
                                 value={formData.projectAddress}
                                 onChange={handleInputChange}
                                 required
                                 className="mt-1"
                                 placeholder="e.g., Barangay Laylay, Boac, Marinduque"
                              />
                              <p className="text-sm text-muted-foreground mt-1">
                                 Enter the complete address (Barangay, Municipality, Province)
                              </p>
                           </div>

                           {formData.requestType === 'Cutting' && (
                              <div>
                                 <Label htmlFor="purpose" className="text-lg font-semibold text-green-700 dark:text-green-500">Purpose</Label>
                                 <Textarea
                                    id="purpose"
                                    name="purpose"
                                    value={formData.purpose}
                                    onChange={handleInputChange}
                                    required
                                    className="mt-2 bg-background"
                                    placeholder="Please state the purpose for your cutting request..."
                                 />
                              </div>
                           )}
                        </div>
                     )}

                     {currentStep === 2 && renderTreeAndLandDetails()}

                     {currentStep === 3 && formData.requestType !== 'Inventory' && (
                        <div className="space-y-4">
                           <p className="text-sm text-gray-700 dark:text-gray-300 mb-4 font-semibold">
                              Please check the boxes that apply to you. In the next step, you will be required to upload the corresponding documents.
                           </p>
                           <div className="space-y-2">
                              <CheckboxItem
                                 id="isWithinSubdivision"
                                 label="Is the property within a subdivision?"
                                 checked={formData.isWithinSubdivision}
                                 onChange={handleCheckboxChange}
                              />
                              <CheckboxItem
                                 id="isSchoolOrOrganization"
                                 label="Is this for a school or organization?"
                                 checked={formData.isSchoolOrOrganization}
                                 onChange={handleCheckboxChange}
                              />
                           </div>
                        </div>
                     )}

                     {currentStep === 4 && (
                        <div className="space-y-4">
                           <div className="h-[500px] overflow-y-auto pr-4 custom-scrollbar">
                              {formData.requestType === 'Inventory' ? (
                                 <UploadCard
                                    label="Letter of Intent"
                                    documentLabel="Upload Letter of Intent (1 original)"
                                    files={formData.files.letterOfIntent}
                                    onFileChange={(e) => handleFileChange(e, 'letterOfIntent')}
                                    onRemoveFile={(file) => removeFile('letterOfIntent', file)}
                                 />
                              ) : (
                                 <>
                                    <UploadCard
                                       label="Application Letter"
                                       documentLabel="Upload Application Letter (1 original)"
                                       files={formData.files.applicationLetter}
                                       onFileChange={(e) => handleFileChange(e, 'applicationLetter')}
                                       onRemoveFile={(file) => removeFile('applicationLetter', file)}
                                    />
                                    <UploadCard
                                       label="LGU Endorsement"
                                       documentLabel="Upload LGU Endorsement (1 original)"
                                       files={formData.files.lguEndorsement}
                                       onFileChange={(e) => handleFileChange(e, 'lguEndorsement')}
                                       onRemoveFile={(file) => removeFile('lguEndorsement', file)}
                                    />
                                    {formData.isWithinSubdivision && (
                                       <UploadCard
                                          label="Homeowner's Resolution"
                                          documentLabel="Upload Homeowner's Resolution (1 original)"
                                          files={formData.files.homeownersResolution}
                                          onFileChange={(e) => handleFileChange(e, 'homeownersResolution')}
                                          onRemoveFile={(file) => removeFile('homeownersResolution', file)}
                                       />
                                    )}
                                    {formData.isSchoolOrOrganization && (
                                       <UploadCard
                                          label="PTA Resolution"
                                          documentLabel="Upload PTA Resolution or Resolution from any organize group of No Objection and Reason for Cutting (1 original)"
                                          files={formData.files.ptaResolution}
                                          onFileChange={(e) => handleFileChange(e, 'ptaResolution')}
                                          onRemoveFile={(file) => removeFile('ptaResolution', file)}
                                       />
                                    )}
                                 </>
                              )}
                           </div>
                        </div>
                     )}

                     {currentStep === 5 && (
                        <div className="space-y-6 h-[500px] overflow-y-auto pr-4 custom-scrollbar">
                           <div className="review-step-container custom-scrollbar flex-grow overflow-auto">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                 {formData.requestType === 'Cutting' && verifiedInventoryData && (
                                    <div className="col-span-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                                       <h4 className="font-semibold text-green-700 dark:text-green-400 mb-2">
                                          Verified Inventory Request
                                       </h4>
                                       <p className="text-sm text-green-600 dark:text-green-300">
                                          Application Number: {previousApplicationNumber}
                                       </p>
                                    </div>
                                 )}

                                 {Object.entries(formData)
                                    .filter(([key]) =>
                                       key !== 'status' &&
                                       key !== 'dateOfSubmission' &&
                                       key !== 'files' &&
                                       key !== 'documentRequirements'
                                    )
                                    .map(([key, value]) => (
                                       <div key={key} className="bg-background border rounded-lg p-3">
                                          <h4 className="font-semibold text-green-600 dark:text-green-500 mb-1 text-sm">
                                             {formatLabel(key)}
                                          </h4>
                                          <p className="text-foreground text-sm">
                                             {formatReviewValue(key, value)}
                                          </p>
                                       </div>
                                    ))}
                              </div>

                              {formData.requestType !== 'Inventory' && (
                                 <div className="bg-background border rounded-lg p-3 mt-4">
                                    <h4 className="font-semibold text-green-600 dark:text-green-500 mb-2 text-sm">
                                       Document Requirements
                                    </h4>
                                    <div className="space-y-2">
                                       <div className="flex items-center">
                                          <span className="text-foreground text-sm">
                                             Within Subdivision: {formData.isWithinSubdivision ? 'Yes' : 'No'}
                                          </span>
                                       </div>
                                       <div className="flex items-center">
                                          <span className="text-foreground text-sm">
                                             School or Organization: {formData.isSchoolOrOrganization ? 'Yes' : 'No'}
                                          </span>
                                       </div>
                                    </div>
                                 </div>
                              )}

                              <div className="bg-background border rounded-lg p-3 mt-4">
                                 <h4 className="font-semibold text-green-600 dark:text-green-500 mb-2 text-sm">
                                    Uploaded Files
                                 </h4>
                                 <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {Object.entries(formData.files)
                                       .filter(([key]) =>
                                          formData.requestType === 'Inventory'
                                             ? key === 'letterOfIntent'
                                             : key !== 'letterOfIntent'
                                       )
                                       .map(([docType, files]) => (
                                          <div key={docType} className="border-b dark:border-gray-700 pb-2">
                                             <h5 className="font-medium text-foreground mb-1 text-xs">
                                                {formatLabel(docType)}
                                             </h5>
                                             {files.length > 0 ? (
                                                <ul className="list-disc list-inside">
                                                   {files.map((file, index) => (
                                                      <li key={index} className="text-xs text-muted-foreground">
                                                         {file.name}
                                                      </li>
                                                   ))}
                                                </ul>
                                             ) : (
                                                <p className="text-xs text-muted-foreground">No files uploaded</p>
                                             )}
                                          </div>
                                       ))}
                                 </div>
                              </div>
                           </div>
                        </div>
                     )}
                  </form>
               </CardContent>
               <CardFooter className="mt-14 flex flex-col sm:flex-row justify-between items-center gap-4">
                  <div className="w-full sm:w-auto">
                     {currentStep > 0 && (
                        <Button
                           type="button"
                           variant="outline"
                           onClick={handlePrevStep}
                           className="w-full sm:w-auto"
                        >
                           Previous
                        </Button>
                     )}
                  </div>
                  <div className="w-full sm:w-auto flex flex-col sm:flex-row gap-2">
                     {currentStep < steps.length - 1 ? (
                        <Button
                           type="button"
                           onClick={handleNextStep}
                           className="w-full sm:w-auto bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white"
                           disabled={isSubmitting || isSavingDraft}
                        >
                           Next
                        </Button>
                     ) : (
                        <>
                           <Button
                              type="button"
                              variant="outline"
                              onClick={handleSaveAsDraft}
                              className="w-full sm:w-auto"
                              disabled={isSubmitting || isSavingDraft}
                           >
                              {isSavingDraft ? (
                                 <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Saving...
                                 </>
                              ) : (
                                 'Save as Draft'
                              )}
                           </Button>
                           <Button
                              type="submit"
                              onClick={handleSubmit}
                              className="w-full sm:w-auto bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white"
                              disabled={isSubmitting || isSavingDraft}
                           >
                              {isSubmitting ? (
                                 <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Submitting...
                                 </>
                              ) : (
                                 'Submit Application'
                              )}
                           </Button>
                        </>
                     )}
                  </div>
               </CardFooter>
            </Card>
         </div>
         <Modal
            isOpen={modalOpen}
            title={modalContent.title}
            message={modalContent.message}
            onClose={() => setModalOpen(false)}
            onHome={() => navigate('/home')}
            onApplications={() => navigate('/applicationsStatus')}
         />
         <AlertDialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
            <AlertDialogContent>
               <AlertDialogHeader>
                  <AlertDialogTitle>Submit Application</AlertDialogTitle>
                  <AlertDialogDescription>
                     Are you sure you want to submit this application? Please review all details carefully before proceeding.
                  </AlertDialogDescription>
               </AlertDialogHeader>
               <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                     onClick={handleConfirmSubmit}
                     className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white"
                  >
                     {isSubmitting ? (
                        <>
                           <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                           Submitting...
                        </>
                     ) : (
                        'Submit'
                     )}
                  </AlertDialogAction>
               </AlertDialogFooter>
            </AlertDialogContent>
         </AlertDialog>

         <AlertDialog open={showDraftDialog} onOpenChange={setShowDraftDialog}>
            <AlertDialogContent>
               <AlertDialogHeader>
                  <AlertDialogTitle>Save as Draft</AlertDialogTitle>
                  <AlertDialogDescription>
                     Do you want to save your current progress as a draft? You can continue editing it later.
                  </AlertDialogDescription>
               </AlertDialogHeader>
               <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                     onClick={handleConfirmSaveAsDraft}
                     className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white"
                  >
                     {isSavingDraft ? (
                        <>
                           <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                           Saving...
                        </>
                     ) : (
                        'Save Draft'
                     )}
                  </AlertDialogAction>
               </AlertDialogFooter>
            </AlertDialogContent>
         </AlertDialog>
      </div>
   );
};

export default PLTCPForm;
